import * as React from "react";
import { observer } from "mobx-react";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Campaign = CampaignRecommendationsWithOffersQuery.Campaigns;
import { Viewport, ViewSmall, ViewMedium, Layer } from "bernie-view";
import { UitkText, UitkHeading, UitkHeadingProps } from "uitk-react-text";
import { UitkCardAloha, UitkCardTextSegment } from "uitk-react-cards";
import { UitkLink } from "uitk-react-link";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { UitkFigureAspectRatioType as ExperimentalAspectRatioType } from "uitk-react-experimental-images";
import { QueryRoute } from "bernie-core";
import { InfoDialog } from "../InfoDialog/InfoDialog";
import { useLocalization } from "@shared-ui/localization-context";

export interface CampaignHeaderProps {
  campaign: Campaign;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  campaignTitle?: string;
  campaignCopyAlignment?: string;
  showCampaignDescription?: boolean;
  showCampaignTitle?: boolean;
  showCampaignTermsAndConditions?: boolean;
  view?: string;
  imgSrc?: string;
}

// header of the campaigns with title, subtitle and terms and conditions
export const CampaignHeader = observer(
  ({
    campaign,
    openDialog,
    closeDialog,
    campaignTitle,
    campaignCopyAlignment,
    showCampaignDescription,
    showCampaignTitle,
    showCampaignTermsAndConditions,
    view,
    imgSrc,
  }: CampaignHeaderProps) => {
    const { formatText } = useLocalization();
    const isHavingOriginCity = campaign.fromOriginHeaderText;
    const defaultHeadingConfig: UitkHeadingProps = { level: 2, size: 700 };
    const centeredHeadingConfig: UitkHeadingProps = { level: 2, size: 800 };
    const campaignHeading = campaignTitle ? campaignTitle : campaign.headline;
    const isCentered = campaignCopyAlignment === "centered";
    const headingConfig = isCentered ? centeredHeadingConfig : defaultHeadingConfig;
    const showDescription = showCampaignDescription !== undefined ? showCampaignDescription : true;
    const showTitle = showCampaignTitle !== undefined ? showCampaignTitle : true;
    const showTermsAndConditions = showCampaignTermsAndConditions !== undefined ? showCampaignTermsAndConditions : true;
    const image = imgSrc || "";

    if (!showDescription && !showTitle && !showTermsAndConditions) {
      return null;
    }

    if (view === "carousel-vr" && image !== "") {
      const desktopClassName = "all-b-padding-zero vrScrim";
      const scrimCardPropsMobile = {
        id: `vr-campaign-header`,
        className: "",
        titleSlot: <h3 className="uitk-type-800">{campaignHeading}</h3>,
        subtitleSlot: (
          <UitkLayoutFlex direction="column" justifyContent="space-between" style={{ height: "inherit" }}>
            <UitkLayoutFlexItem className="s-t-padding-zero">
              <div>
                <UitkText size={500}>{campaign.description.long}</UitkText>
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        ),
      };

      const scrimCardPropsDesktop = {
        id: `vr-campaign-header`,
        className: desktopClassName,
        titleSlot: (
          <UitkLayoutGrid columns={4} space="two">
            <>
              <UitkLayoutGridItem>
                <h3 className="uitk-type-800 all-l-padding-six">{campaignHeading}</h3>
              </UitkLayoutGridItem>
            </>
          </UitkLayoutGrid>
        ),
        footerSlot: (
          <UitkCardAloha className="vrFooter uitk-card-aloha-roundcorner-top-right uitk-card-aloha-roundcorner-top-left uitk-flex">
            <UitkCardTextSegment>
              <span className="all-l-padding-three all-y-padding-three">{campaign.description.long}</span>
            </UitkCardTextSegment>
          </UitkCardAloha>
        ),
      };

      return (
        <Viewport>
          <ViewSmall>
            <ScrimCard
              {...scrimCardPropsMobile}
              aspectRatio={ExperimentalAspectRatioType.R1_1}
              backgroundImageURL={image}
            />
          </ViewSmall>
          <ViewMedium>
            <ScrimCard
              {...scrimCardPropsDesktop}
              aspectRatio={ExperimentalAspectRatioType.R4_1}
              backgroundImageURL={image}
            />
          </ViewMedium>
        </Viewport>
      );
    }

    return (
      <div
        className={isCentered ? "center-campaignCopyAlignment all-t-padding-three" : "CampaignHeader"}
        data-testid="campaign-header"
      >
        {showTitle && <UitkHeading {...headingConfig}>{campaignHeading}</UitkHeading>}
        <div className={isCentered ? "all-t-padding-one" : ""}>
          <CampaignDescription
            campaign={campaign}
            isCentered={isCentered}
            isHavingOriginCity={isHavingOriginCity}
            showDescription={showDescription}
          />
          {campaign.termsAndConditions && showTermsAndConditions && (
            <>
              <UitkLink
                className={isCentered ? "center_termsAndConditions" : "terms-conditions-text"}
                inline={isCentered ? false : true}
                onClick={openDialog.bind(this, `terms-conditions-${campaign.id}`)}
              >
                <button>{formatText("merchCampaign.termsConditions.label")}</button>
              </UitkLink>
              <QueryRoute query={{ pwaDialog: `terms-conditions-${campaign.id}` }}>
                <Layer id={`terms-conditions-dialog-${campaign.id}`}>
                  <InfoDialog
                    infoText={campaign.termsAndConditions}
                    closeDialog={closeDialog}
                    buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
                  />
                </Layer>
              </QueryRoute>
            </>
          )}
        </div>
      </div>
    );
  }
);

interface CampaignDescriptionProps {
  campaign: Campaign;
  isCentered: boolean;
  isHavingOriginCity?: string | null;
  showDescription: boolean;
}

const CampaignDescription: React.FC<CampaignDescriptionProps> = ({
  campaign,
  isCentered,
  isHavingOriginCity,
  showDescription,
}) => {
  if (!showDescription) {
    return null;
  }

  return (
    <>
      {campaign.description.long && (
        <span
          className={
            isCentered ? "all-r-margin-one uitk-type-paragraph-400" : "all-r-margin-one uitk-type-paragraph-300"
          }
        >
          {campaign.description.long}
        </span>
      )}
      {isHavingOriginCity && (
        <span className={isCentered ? "uitk-type-paragraph-400" : "all-r-margin-one uitk-type-paragraph-300"}>
          {`${campaign.fromOriginHeaderText}.`}
        </span>
      )}
    </>
  );
};
